import React, { useEffect, useState } from 'react';
import BrandLayout from '@/components/BrandLayout';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Package, X, ChevronRight } from 'lucide-react';

const STATUSES = ['placed', 'accepted', 'packed', 'ready', 'out_for_delivery', 'delivered', 'cancelled'];
const STATUS_LABEL = {
    placed: 'New', accepted: 'Accepted', packed: 'Packed', ready: 'Ready',
    out_for_delivery: 'Out for Delivery', delivered: 'Delivered', cancelled: 'Cancelled',
};
const STATUS_COLOR = {
    placed: 'pill-orange', accepted: 'pill-blue', packed: 'pill-blue',
    ready: 'pill-yellow', out_for_delivery: 'pill-yellow', delivered: 'pill-blue', cancelled: 'pill-outline',
};

export default function BrandOrders() {
    const [orders, setOrders] = useState([]);
    const [filter, setFilter] = useState('');
    const [selected, setSelected] = useState(null);
    const [busy, setBusy] = useState(false);

    const load = () => {
        const q = filter ? `?status=${filter}` : '';
        api.get(`/brand/orders${q}`).then(({ data }) => setOrders(data)).catch(() => {});
    };
    useEffect(load, [filter]);

    const updateStatus = async (orderId, status) => {
        setBusy(true);
        try {
            const { data } = await api.post(`/brand/orders/${orderId}/status`, { status });
            toast.success(`Order ${data.order_number} → ${STATUS_LABEL[status]}`);
            setSelected(data);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Failed to update');
        } finally { setBusy(false); }
    };

    return (
        <BrandLayout>
            <div className="p-10" data-testid="brand-orders-page">
                <div className="mb-8 flex flex-wrap items-end justify-between gap-6">
                    <div>
                        <div className="overline">Order Management</div>
                        <h1 className="font-serif text-4xl text-inorbit-blue mt-2">Orders</h1>
                        <div className="text-sm text-text-secondary mt-1">{orders.length} order{orders.length !== 1 ? 's' : ''} {filter && `• ${STATUS_LABEL[filter]}`}</div>
                    </div>
                    <div className="flex gap-2 flex-wrap" data-testid="status-filter">
                        <button onClick={() => setFilter('')} className={`pill ${!filter ? 'pill-blue' : 'pill-outline'}`} data-testid="filter-all">All</button>
                        {STATUSES.map((s) => (
                            <button key={s} onClick={() => setFilter(s)} className={`pill ${filter === s ? 'pill-blue' : 'pill-outline'}`} data-testid={`filter-${s}`}>
                                {STATUS_LABEL[s]}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="bg-white border border-border-subtle rounded-md overflow-hidden">
                    {orders.length === 0 ? (
                        <div className="p-16 text-center text-text-secondary">No orders found.</div>
                    ) : (
                        <table className="w-full text-sm" data-testid="orders-table">
                            <thead className="bg-cream text-text-secondary text-[11px] uppercase tracking-widest">
                                <tr>
                                    <th className="text-left p-4">Order #</th>
                                    <th className="text-left p-4">Customer</th>
                                    <th className="text-left p-4">Items</th>
                                    <th className="text-right p-4">Total</th>
                                    <th className="text-left p-4">Status</th>
                                    <th className="text-left p-4">Placed</th>
                                    <th className="p-4"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {orders.map((o) => (
                                    <tr key={o.id} className="border-t border-border-subtle hover:bg-cream/60" data-testid={`order-row-${o.id}`}>
                                        <td className="p-4 font-mono text-xs text-inorbit-blue">{o.order_number}</td>
                                        <td className="p-4"><div className="text-inorbit-blue">{o.customer_name}</div><div className="text-xs text-text-tertiary">{o.customer_email}</div></td>
                                        <td className="p-4 text-text-secondary">{o.items?.length || 0}</td>
                                        <td className="p-4 text-right font-semibold text-inorbit-blue">₹{Number(o.total).toLocaleString('en-IN')}</td>
                                        <td className="p-4"><span className={`pill ${STATUS_COLOR[o.status] || 'pill-outline'}`}>{STATUS_LABEL[o.status] || o.status}</span></td>
                                        <td className="p-4 text-text-tertiary text-xs">{new Date(o.created_at).toLocaleString('en-IN')}</td>
                                        <td className="p-4 text-right"><button onClick={() => setSelected(o)} className="text-inorbit-orange hover:text-inorbit-blue" data-testid={`view-order-${o.id}`}><ChevronRight size={18} /></button></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>

            {selected && (
                <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-6" onClick={() => setSelected(null)} data-testid="order-detail-modal">
                    <div className="bg-white max-w-2xl w-full max-h-[90vh] overflow-y-auto rounded-md" onClick={(e) => e.stopPropagation()}>
                        <div className="p-8 border-b border-border-subtle flex items-start justify-between">
                            <div>
                                <div className="overline">Order</div>
                                <div className="font-serif text-3xl text-inorbit-blue mt-1">{selected.order_number}</div>
                                <div className="text-sm text-text-secondary mt-1">{selected.customer_name} • {selected.customer_email}</div>
                                <div className="mt-3"><span className={`pill ${STATUS_COLOR[selected.status]}`}>{STATUS_LABEL[selected.status]}</span></div>
                            </div>
                            <button onClick={() => setSelected(null)} className="text-text-tertiary hover:text-inorbit-blue" data-testid="close-order-modal"><X size={24} /></button>
                        </div>

                        <div className="p-8 border-b border-border-subtle">
                            <div className="overline mb-4">Items</div>
                            <div className="space-y-3">
                                {selected.items?.map((it, i) => (
                                    <div key={i} className="flex items-center gap-4">
                                        <div className="w-14 h-14 bg-cream overflow-hidden rounded">
                                            {it.image && <img src={it.image} alt="" className="w-full h-full object-cover" />}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="text-sm text-inorbit-blue truncate">{it.name}</div>
                                            <div className="text-xs text-text-tertiary">SKU {it.sku} • Qty {it.quantity}</div>
                                        </div>
                                        <div className="font-semibold text-inorbit-blue">₹{Number(it.line_total).toLocaleString('en-IN')}</div>
                                    </div>
                                ))}
                            </div>
                            <div className="flex justify-between mt-6 pt-6 border-t border-border-subtle">
                                <div>
                                    <div className="text-xs text-text-tertiary">Payment</div>
                                    <div className="font-mono text-sm text-inorbit-blue">{selected.payment_id}</div>
                                </div>
                                <div className="text-right">
                                    <div className="text-xs text-text-tertiary">Total</div>
                                    <div className="font-serif text-2xl text-inorbit-blue">₹{Number(selected.total).toLocaleString('en-IN')}</div>
                                </div>
                            </div>
                        </div>

                        <div className="p-8">
                            <div className="overline mb-4">Update Status</div>
                            <div className="flex flex-wrap gap-2">
                                {STATUSES.map((s) => (
                                    <button
                                        key={s}
                                        disabled={busy || s === selected.status}
                                        onClick={() => updateStatus(selected.id, s)}
                                        className={`pill ${selected.status === s ? 'pill-blue' : 'pill-outline'} disabled:opacity-40`}
                                        data-testid={`set-status-${s}`}
                                    >
                                        {STATUS_LABEL[s]}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </BrandLayout>
    );
}
